import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import React, {
  createContext,
  ReducerState,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { useLocation } from 'react-router-dom';
import { createReducer, useLocalStorage } from 'react-use';
import { Loader } from '../../components/Loader';
import {
  GetUserAccessControlDocument,
  GetUserAccessControlQuery,
  GetUserIdentitiesDocument,
  GetUserIdentitiesQuery,
  IdentityFragment,
  IdentityFragmentDoc,
  UserIdentity,
  EResourceType,
  ERoleType,
} from '../../services/graphql/types/graphql';
import { FragmentType, getFragmentData } from '../../services/graphql/types';
import { isEmpty } from 'lodash';
import { CasdoorContext } from './casdoor.context';
import { getDefaultSchoolId } from '../../config/tenant';
import {
  getTeacherPortalView,
  setTeacherPortalView,
  TeacherPortalView,
} from '../../utils/teacherPortalView';
import { clearIdentitySelectionStorage } from '../../utils/identityStorage';
import { IdentityUnavailableState } from '../../components/IdentityUnavailableState';

export const IdentityContext = createContext<null | {
  userIdentities: GetUserIdentitiesQuery;
  uac: GetUserAccessControlQuery['user']['accessControls'];
  currentIdentity: IdentityFragment | null | undefined;
  /** 当前班级的全部身份（支持同班既是任课教师又是班主任） */
  currentClassIdentities: IdentityFragment[];
  /** 双身份用户当前选择的视图：班主任(gradebook) 或 任课教师(teacher) */
  portalView: TeacherPortalView;
  setPortalView: (view: TeacherPortalView) => void;

  availableClasses?: NonNullable<GetUserIdentitiesQuery['currentSchool']>['classes'];
  currentClassId: string;
  setCurrentClassId: ReturnType<typeof useLocalStorage<string>>[1];
  currentClass?: NonNullable<GetUserIdentitiesQuery['currentSchool']>['classes'][0];

  availableSchools?: GetUserIdentitiesQuery['schools'];
  currentSchoolId: string;
  setCurrentSchoolId: (schoolId?: string) => void;
  currentSchool?: GetUserIdentitiesQuery['currentSchool'];

  availableSemesters?: GetUserIdentitiesQuery['semesters'];
  currentSemesterId: string | undefined;
  setCurrentSemesterId: ReturnType<typeof useLocalStorage<string>>[1];
  currentSemester?: GetUserIdentitiesQuery['semesters'][0];

  availableCourses?: GetUserIdentitiesQuery['user']['userCourses'];
  currentCourseId: string;
  setCurrentCourseId: ReturnType<typeof useLocalStorage<string>>[1];
  currentCourse?: GetUserIdentitiesQuery['user']['userCourses'][0];
}>(null);

export const IdentityContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { logout } = useContext(CasdoorContext);
  const location = useLocation();

  const defaultSchoolId = getDefaultSchoolId();

  // 当前学校支持运行时切换：优先 localStorage 中的选择，其次环境变量默认学校
  const lsCurrentSchoolIdStorage = useLocalStorage('CurrentSchoolId', '');
  const [storedSchoolId, setStoredSchoolId] = lsCurrentSchoolIdStorage;
  const currentSchoolId = storedSchoolId || defaultSchoolId;

  const {
    loading,
    error,
    data: userIdentities,
  } = useQuery(GetUserIdentitiesDocument, {
    variables: { schoolId: currentSchoolId },
    skip: !currentSchoolId,
    fetchPolicy: 'network-only', // Force fresh fetch, bypass cache
  });

  // Effect: 监控用户身份查询错误，将错误上报到 Sentry 监控系统
  useEffect(() => {
    if (error) {
      Sentry.captureException(error);
    }
  }, [error]);

  // Effect: 用户身份信息加载完成后进行校验（注释掉的代码：如果当前学校为空则登出）
  useEffect(() => {
    if (loading) {
      return;
    }
    // if (isEmpty(userIdentities?.currentSchool)) {
    //   logout?.(false);
    // }
  }, [userIdentities, logout, loading]);

  const {
    loading: uacLoading,
    data: uacData,
    error: uacError,
  } = useQuery(GetUserAccessControlDocument, {
    fetchPolicy: 'network-only', // Force fresh fetch, bypass cache
  });
  const uac = useMemo(() => uacData?.user?.accessControls, [uacData]);

  /** 教师端可选学校：仅含用户有班级 membership 的学校（global admin 也不展示空壳学校） */
  const teacherPortalSchools = useMemo(() => {
    const all = userIdentities?.schools ?? [];
    const memberIds = userIdentities?.user?.memberSchoolIds ?? [];
    if (memberIds.length === 0) {
      return all;
    }
    const memberSet = new Set(memberIds);
    return all.filter((s) => memberSet.has(s.schoolId));
  }, [userIdentities]);

  const availableSchools = teacherPortalSchools;

  const currentSchool = useMemo(() => userIdentities?.currentSchool, [userIdentities]);

  // Semester
  const lsCurrentSemesterIdStorage = useLocalStorage('CurrentSemesterId', '');
  const [currentSemesterId, setCurrentSemesterId] = lsCurrentSemesterIdStorage;

  // Filter semesters by current school - only show semesters that have lessons in the current school
  const availableSemesters = useMemo(() => {
    if (!currentSchool || !userIdentities?.semesters) {
      console.warn('availableSemesters: missing data', {
        hasCurrentSchool: !!currentSchool,
        hasSemesters: !!userIdentities?.semesters,
      });
      return [];
    }

    // Get all semester IDs that are used in this school's classes
    const allLessonSchedules = currentSchool.classes.flatMap((c) => c.lessonSchedule ?? []);
    const semesterIdsInSchool = new Set(
      allLessonSchedules.map((ls) => ls.semester?.semesterId).filter((id): id is string => !!id),
    );

    // Filter semesters to only include those used in this school (strict filtering)
    const filtered = userIdentities.semesters.filter((semester) =>
      semesterIdsInSchool.has(semester.semesterId),
    );

    if (filtered.length === 0) {
      console.warn('No semesters found for this school (strict filtering - no lesson schedules)');
    }
    // Stable sort by semesterId so "first" option does not change between renders
    return [...filtered].sort((a, b) => (a.semesterId ?? '').localeCompare(b.semesterId ?? ''));
  }, [currentSchool, userIdentities]);

  const currentSemester = useMemo(
    () => availableSemesters?.find((s) => s.semesterId === currentSemesterId),
    [availableSemesters, currentSemesterId],
  );

  //Class
  const lsCurrentClassIdStorage = useLocalStorage('CurrentClassId', '');
  const [currentClassId, setCurrentClassId] = lsCurrentClassIdStorage;

  const currentClass = useMemo(
    () => currentSchool?.classes.find((c) => c.classId === currentClassId),
    [currentSchool, currentClassId],
  );

  // Course
  const lsCurrentCourseIdStorage = useLocalStorage('CurrentCourseId', '');
  const [currentCourseId, setCurrentCourseId] = lsCurrentCourseIdStorage;

  // 切换学校：清理班级/课程/学期的本地选择，避免跨校脏数据；随后下游 effect 会自动选中新学校的第一项
  const setCurrentSchoolId = React.useCallback(
    (schoolId?: string) => {
      if (!schoolId || schoolId === currentSchoolId) {
        return;
      }
      clearIdentitySelectionStorage();
      setCurrentClassId('');
      setCurrentCourseId('');
      setCurrentSemesterId('');
      setStoredSchoolId(schoolId);
    },
    [
      currentSchoolId,
      setCurrentClassId,
      setCurrentCourseId,
      setCurrentSemesterId,
      setStoredSchoolId,
    ],
  );

  // 当存储/默认学校不在教师端可选学校列表时，自动纠正到第一个有 membership 的学校
  useEffect(() => {
    const schools = teacherPortalSchools;
    if (!schools || schools.length === 0) {
      return;
    }
    const schoolIds = schools.map((s) => s.schoolId);
    if (schoolIds.includes(currentSchoolId)) {
      return;
    }
    const next =
      schools.length === 1
        ? schools[0].schoolId
        : schoolIds.includes(defaultSchoolId)
          ? defaultSchoolId
          : schools[0].schoolId;
    clearIdentitySelectionStorage();
    setCurrentClassId('');
    setCurrentCourseId('');
    setCurrentSemesterId('');
    setStoredSchoolId(next);
  }, [teacherPortalSchools, currentSchoolId, defaultSchoolId, setStoredSchoolId, setCurrentClassId, setCurrentCourseId, setCurrentSemesterId]);

  const availableCourses = useMemo(() => {
    const resultCourse = userIdentities?.user.userCourses.filter(({ course }) => course?.courseId);
    const filtered =
      resultCourse?.length !== 1
        ? resultCourse?.filter((c) => c.course.courseId !== '1e25cc71-a3e0-4a70-a574-5322a8e95fe5')
        : resultCourse;
    // Stable sort by courseId so "first" option does not change between renders
    return [...(filtered ?? [])].sort((a, b) =>
      (a.course.courseId ?? '').localeCompare(b.course.courseId ?? ''),
    );
  }, [userIdentities]);

  const currentCourse = useMemo(() => {
    return availableCourses?.find((c) => c.course.courseId === currentCourseId);
  }, [availableCourses, currentCourseId]);

  // Effect: 课程切换后打印日志信息（仅用于调试/追踪）
  useEffect(() => {
    if (!currentCourse) {
      return;
    }
    console.info(`Course switched to ${currentCourse.course.name}`);
  }, [currentCourse]);

  // 双身份视图选择（班主任 gradebook / 任课教师 teacher），初始读取 sessionStorage
  const [portalView, setPortalViewState] = React.useState<TeacherPortalView>(
    () => getTeacherPortalView() ?? 'gradebook',
  );
  const setPortalView = React.useCallback((view: TeacherPortalView) => {
    setTeacherPortalView(view);
    setPortalViewState(view);
  }, []);

  const currentClassIdentities = useMemo(() => {
    const clas = currentSchool?.classes.find((c) => c.classId === currentClassId);
    const all = (clas?.selfIdentities ?? [])
      .map((si) => getFragmentData(IdentityFragmentDoc, si))
      .filter((i): i is IdentityFragment => !!i);
    if (all.length > 0) {
      return all;
    }
    // 兼容：旧字段 selfIdentity 单值
    const single = getFragmentData(IdentityFragmentDoc, clas?.selfIdentity);
    return single ? [single] : [];
  }, [currentClassId, currentSchool]);

  const currentIdentity = useMemo(() => {
    const teacher = currentClassIdentities.find((i) => i.identity === 'teacher');
    const ta = currentClassIdentities.find((i) => i.identity === 'ta');
    // 同班双身份：按当前视图选择生效身份
    if (teacher && ta) {
      return portalView === 'teacher' ? teacher : ta;
    }
    // 单身份：按优先级 ta > teacher > 其他
    return ta ?? teacher ?? currentClassIdentities[0];
  }, [currentClassIdentities, portalView]);

  const availableClasses = useMemo(() => {
    const schoolClasses = currentSchool?.classes;

    if (!schoolClasses || !currentSemesterId) {
      return schoolClasses ?? [];
    }

    // Filter classes by current semester through LessonSchedule
    const classIdsInSemester = new Set(
      schoolClasses
        .flatMap((c) => c.lessonSchedule ?? [])
        .filter((ls) => ls.semester?.semesterId === currentSemesterId)
        .map((ls) => ls.classId),
    );

    const filtered = schoolClasses.filter((c) => classIdsInSemester.has(c.classId));
    // Stable sort by classId so "first" option does not change between renders (avoids CurrentClassId/CourseId jumping)
    return [...filtered].sort((a, b) => (a.classId ?? '').localeCompare(b.classId ?? ''));
  }, [currentSchool, currentSemesterId]);

  // Refs: 用于追踪上一次同步的值，避免循环依赖
  const prevCourseIdRef = React.useRef<string | undefined>(currentCourseId);
  const prevClassIdRef = React.useRef<string | undefined>(currentClassId);
  const isSyncingRef = React.useRef(false);

  // Effect: 合并处理课程和班级的同步逻辑，避免循环依赖
  useEffect(() => {
    // 防止重复同步
    if (isSyncingRef.current) return;

    // 如果数据还没准备好，跳过
    if (!availableCourses?.length || !availableClasses?.length) return;

    let targetCourseId = currentCourseId;
    let targetClassId = currentClassId;
    let hasChanges = false;

    // 步骤1: 确保课程 ID 有效
    const validCourseIds = availableCourses.map((c) => c.course.courseId);
    if (!targetCourseId || !validCourseIds.includes(targetCourseId)) {
      targetCourseId = availableCourses[0]?.course.courseId;
      hasChanges = true;
      if (import.meta.env.DEV) console.info('[Identity] reset course to first available');
    }

    // 步骤2: 确保班级 ID 有效
    const validClassIds = availableClasses.map((c) => c.classId);
    if (!targetClassId || !validClassIds.includes(targetClassId)) {
      targetClassId = availableClasses[0]?.classId;
      hasChanges = true;
      if (import.meta.env.DEV) console.info('[Identity] reset class to first available');
    }

    // 步骤3: 获取目标班级对象
    const targetClass = availableClasses.find((c) => c.classId === targetClassId);

    // 步骤4: 检查课程和班级的匹配关系
    if (targetClass && targetCourseId) {
      const classCourseIds = targetClass.courses.map((c) => c.courseId);

      if (!classCourseIds.includes(targetCourseId)) {
        // 课程和班级不匹配，需要同步
        const courseChanged = prevCourseIdRef.current !== targetCourseId;
        const classChanged = prevClassIdRef.current !== targetClassId;

        if (courseChanged && !classChanged) {
          // 用户切换了课程 -> 保持课程，切换到包含该课程的班级
          const matchingClass = availableClasses.find((c) =>
            c.courses.some((course) => course.courseId === targetCourseId),
          );
          if (matchingClass) {
            targetClassId = matchingClass.classId;
            hasChanges = true;
            if (import.meta.env.DEV) console.info('[Identity] switch class to match course');
          }
        } else {
          // 班级变化或初始化 -> 保持班级，切换到该班级的第一个课程
          const sorted = [...(targetClass.courses ?? [])].sort((a, b) =>
            (a.courseId ?? '').localeCompare(b.courseId ?? ''),
          );
          const newCourseId = sorted[0]?.courseId;
          if (newCourseId && validCourseIds.includes(newCourseId)) {
            targetCourseId = newCourseId;
            hasChanges = true;
            if (import.meta.env.DEV) console.info('[Identity] switch course to match class');
          }
        }
      }
    }

    // 应用更改
    if (hasChanges) {
      isSyncingRef.current = true;

      if (targetCourseId !== currentCourseId) {
        setCurrentCourseId(targetCourseId);
      }
      if (targetClassId !== currentClassId) {
        setCurrentClassId(targetClassId);
      }

      // 更新 refs
      prevCourseIdRef.current = targetCourseId;
      prevClassIdRef.current = targetClassId;

      // 允许下一次同步
      setTimeout(() => {
        isSyncingRef.current = false;
      }, 0);
    } else {
      // 更新 refs 以追踪变化
      prevCourseIdRef.current = currentCourseId;
      prevClassIdRef.current = currentClassId;
    }
  }, [
    availableCourses,
    availableClasses,
    currentCourseId,
    currentClassId,
    setCurrentCourseId,
    setCurrentClassId,
  ]);

  // Effect: 身份（学校/班级/角色）切换后打印日志信息（仅用于调试/追踪）
  useEffect(() => {
    if (!currentIdentity || !currentSchool || !currentClass) {
      return;
    }
    console.info(
      `Identity switched to ${currentSchool.name} ${currentClass.name} ${currentIdentity.identity} ${currentIdentity.userId}`,
    );
  }, [currentIdentity, currentSchool, currentClass]);

  // Effect: 当存储的学期不在可用列表中时（例如学校变更后），重置为第一个可用学期
  useEffect(() => {
    if (!availableSemesters || availableSemesters.length === 0) {
      return;
    }
    if (!availableSemesters.map((s) => s.semesterId).includes(currentSemesterId ?? '')) {
      setCurrentSemesterId(availableSemesters[0]?.semesterId);
    }
  }, [availableSemesters, currentSemesterId, setCurrentSemesterId]);

  // Ref: 用于追踪学期变化，以便在手动切换学期时重置班级
  const prevSemesterId = React.useRef<string | undefined>(currentSemesterId);

  // Effect: 当学期手动变更时（非初始加载），自动重置为第一个可用班级
  useEffect(() => {
    if (
      prevSemesterId.current &&
      prevSemesterId.current !== currentSemesterId &&
      availableClasses &&
      availableClasses.length > 0
    ) {
      setCurrentClassId(availableClasses[0]?.classId);
    }
    prevSemesterId.current = currentSemesterId;
  }, [currentSemesterId, availableClasses, setCurrentClassId]);

  // Effect: 用户身份信息加载完成后，初始化课程 ID（如果尚未设置）
  // 注意：此 effect 仅处理初始加载时的默认值设置，后续的同步逻辑由上面的合并 effect 处理
  useEffect(() => {
    if (!userIdentities) {
      return;
    }

    // School ID is now fixed, no need to set it dynamically
    // if (!currentSchoolId) {
    //   setCurrentSchoolId(userIdentities.schools[0]?.schoolId);
    // }

    // 只在完全没有课程数据时设置默认值（避免与合并 effect 冲突）
    if (!currentCourseId && !availableCourses?.length) {
      setCurrentCourseId(userIdentities.user.userCourses[0]?.course.courseId);
    }
  }, [userIdentities]);

  // Effect: 学校设置完成后，初始化学期 ID（如果尚未设置）
  useEffect(() => {
    if (!availableSemesters || availableSemesters.length === 0) {
      return;
    }
    if (!currentSemesterId) {
      setCurrentSemesterId(availableSemesters[0]?.semesterId);
    }
  }, [availableSemesters, currentSemesterId, setCurrentSemesterId]);

  const videoCourseId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('courseId')?.trim() ?? '';
  }, [location.search]);

  const isVideoCourseRoute = location.pathname.includes('/vcourse');

  const hasVideoCourseAccess = useMemo(() => {
    if (!videoCourseId || !uac?.length) {
      return false;
    }
    return uac.some(
      (item) =>
        item.resourceType === EResourceType.VideoCourse &&
        item.resourceId === videoCourseId &&
        item.role === ERoleType.Student,
    );
  }, [uac, videoCourseId]);

  const videoCourseContextReady =
    isVideoCourseRoute &&
    videoCourseId &&
    !loading &&
    !uacLoading &&
    userIdentities &&
    uac !== undefined &&
    hasVideoCourseAccess;

  if (videoCourseContextReady) {
    return (
      <IdentityContext.Provider
        value={{
          userIdentities,
          uac,
          currentIdentity: null,
          currentClassIdentities: [],
          portalView,
          setPortalView,
          currentClassId: '',
          setCurrentClassId,
          availableClasses: [],
          currentSchoolId: defaultSchoolId,
          setCurrentSchoolId,
          availableSchools: teacherPortalSchools,
          currentSchool: userIdentities.currentSchool ?? undefined,
          currentSemesterId: undefined,
          setCurrentSemesterId,
          availableSemesters: [],
          currentSemester: undefined,
          currentCourseId: videoCourseId,
          setCurrentCourseId,
          availableCourses: userIdentities.user.userCourses,
          currentCourse: userIdentities.user.userCourses.find(
            (c) => c.course.courseId === videoCourseId,
          ),
        }}
      >
        {children}
      </IdentityContext.Provider>
    );
  }

  if (loading || uacLoading) {
    return <Loader source="IdentityProvider" fullscreen />;
  }

  if (!userIdentities || uac === undefined) {
    return <Loader source="IdentityProvider" fullscreen />;
  }

  const noAccessibleClasses =
    Boolean(currentSchool) && availableClasses.length === 0;

  if (noAccessibleClasses) {
    return (
      <IdentityUnavailableState
        schoolName={currentSchool?.name}
        availableSchools={teacherPortalSchools}
        currentSchoolId={currentSchoolId}
        onSchoolChange={setCurrentSchoolId}
      />
    );
  }

  const identityReady =
    currentClassId &&
    currentSchoolId &&
    currentCourseId &&
    currentIdentity &&
    currentClass;

  if (!identityReady) {
    if (import.meta.env.DEV) {
      console.info('[IdentityProvider] waiting for identity sync', {
        currentClassId,
        currentSchoolId,
        currentCourseId,
        hasIdentity: !!currentIdentity,
        hasClass: !!currentClass,
      });
    }
    return <Loader source="IdentityProvider" fullscreen />;
  }

  return (
    <IdentityContext.Provider
      value={{
        userIdentities,
        uac,
        currentIdentity,
        currentClassIdentities,
        portalView,
        setPortalView,

        currentClassId,
        currentClass,
        setCurrentClassId,
        availableClasses,

        currentSchoolId,
        currentSchool,
        setCurrentSchoolId,
        availableSchools,

        currentSemesterId,
        currentSemester,
        setCurrentSemesterId,
        availableSemesters,

        currentCourseId,
        currentCourse,
        setCurrentCourseId,
        availableCourses,
      }}
    >
      {children}
    </IdentityContext.Provider>
  );
};
